import { APIClient } from "./api_helper";
import * as url from "./url_helper";

const api = new APIClient();
// Gets the logged in user data from local session

// Login Method
export const postJwtLogin = (data) => api.create(url.POST_JWT_LOGIN, data);

// Boot-time token validation; resolves the user's current identity + role, or
// rejects (401) if the stored token is missing/expired/invalid.
export const getAuthMe = () => api.get(url.GET_AUTH_ME);

// Change the logged-in user's password (KAN-92). The current user is resolved
// server-side from the JWT (x-access-token); only currentPassword/newPassword
// are sent.
export const changePassword = (data: {
  currentPassword: string;
  newPassword: string;
}) => api.create(url.POST_AUTH_CHANGE_PASSWORD, data);

// Self-service notification preferences (currently just the daily task
// digest opt-out). Current value comes from getAuthMe()'s dailyDigestOptOut
// field — no separate GET needed.
export const updateMyPreferences = (data: { dailyDigestOptOut: boolean }) =>
  api.update(url.PUT_AUTH_ME_PREFERENCES, data);

// SMS multi-factor, self-service enrolment for the signed-in user. The login
// screen's own verify/resend calls use raw fetch (there is no token yet at that
// point), so they deliberately don't go through this axios client.
export const getMfaStatus = () => api.get(url.GET_AUTH_MFA_STATUS);
export const startMfaSetup = (data: { mobile: string }) =>
  api.create(url.POST_AUTH_MFA_SETUP_START, data);
export const confirmMfaSetup = (data: { challengeId: string; code: string }) =>
  api.create(url.POST_AUTH_MFA_SETUP_CONFIRM, data);
export const disableMfa = (data: { password: string }) =>
  api.create(url.POST_AUTH_MFA_DISABLE, data);

// Super-admin client switcher. Both require a super-admin token (the axios
// x-access-token header is set from the current session).
export const getAdminAgencies = () => api.get(url.GET_ADMIN_AGENCIES);
export const impersonateClient = (clientId: string) =>
  api.create(url.POST_ADMIN_IMPERSONATE, { clientId });

// Register Method
export const postJwtRegister = (url, data) => {
  return api.create(url, data).catch((err) => {
    var message;
    if (err.response && err.response.status) {
      switch (err.response.status) {
        case 404:
          message = "Sorry! the page you are looking for could not be found";
          break;
        case 500:
          message =
            "Sorry! something went wrong, please contact our support team";
          break;
        case 401:
          message = "Invalid credentials";
          break;
        default:
          message = err[1];
          break;
      }
    }
    throw message;
  });
};

/**
 * Returns the authenticated user
 */

export const getAuthenticatedUser = () => {
  var res = localStorage.getItem("authUser");
  if (res) {
    return JSON.parse(res);
  } else {
    return null;
  }
};

export const updateAuthenticatedUser = (user) => {
  localStorage.setItem("authUser", JSON.stringify(user));
};

// Gets the logged in user data from local session
export const getLoggedInUser = () => {
  const user = localStorage.getItem("__accessToken");
  return user;
  // if (user) return JSON.parse(user);
  // return null;
};

// //is user is logged in
export const isUserAuthenticated = () => {
  return getLoggedInUser() !== null;
};

// Media
// Updated to send JSON instead of FormData
export const postMediaCreate = (data) =>
  api.postJson(url.POST_MEDIA_CREATE, data);

export const getHeadingImages = (id) =>
  api.get(url.GET_HEADING_IMAGES + "/" + id, null);
export const getImageById = (id) =>
  api.get(url.GET_IMAGE_BY_ID + "/" + id, null);

export const downloadMedia = (id) => {
  if (id !== null && id !== undefined) {
    return process.env.REACT_APP_API_NEW_URL + "/api/media/fileById/" + id;
  }
  return "";
};
// Assuming the api.get function is adjusted to accept a params object correctly
export const getPresignedUrl = (fileName: string, fileType: string) => {
  // Construct the query string
  const queryString = new URLSearchParams({ fileName, fileType }).toString();
  // Call the existing `get` method with the constructed URL
  return api.get(`${url.GET_PRESIGNED_URL}?${queryString}`);
};

export const deleteMedia = (id) =>
  api.delete(url.DELETE_MEDIA + "/" + id, null);

// Fetch a stored S3 image's bytes through our API so the browser can read
// them. The buckets don't send CORS headers, so a direct fetch(url) from the
// portal is blocked — anything that needs pixels (e.g. the avatar cropper's
// canvas) must go through here. The API returns { contentType, data } with the
// bytes base64-encoded (raw binary gets mangled by API Gateway unless the
// Accept header matches its binaryMediaTypes, which browsers' */* doesn't);
// decode back to a Blob here.
export const getMediaProxy = async (mediaUrl: string): Promise<Blob> => {
  const res: any = await api.get(url.GET_MEDIA_PROXY, { url: mediaUrl });
  const bytes = atob(res?.data || "");
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: res?.contentType || "image/jpeg" });
};

//CLIENT REVIEW

export const getClientReviews = (userId) =>
  api.get(url.GET_ALL_REVIEWS + "/" + userId, null);

export const getClientReview = (id) =>
  api.get(url.GET_ONE_REVIEW_BY_ID + "/" + id, null);

export const addClientReview = (data) =>
  api.create(url.POST_CREAT_REVIEW, data);

export const updateClientReview = (id, data) =>
  api.update(url.UPDATE_CLIENT_REVIEW + "/" + id, data);

export const deleteClientReview = (id) =>
  api.delete(url.DELETE_REVIEW + "/" + id, null);

//USER

export const getUserInfos = (data) => api.create(url.POST_GET_USER_INFO, data);

export const uploadListingREA = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["AgentId"],
    portalType: "REA",
  });
/**
 * REAXML export. Deliberately sends ONLY the listing ids (or a scope) — the
 * agency (tenant) is taken from the auth token server-side, not from anything
 * the client can set, so an owner can't export another agency's listings by
 * changing a field.
 *
 * `scope: "active"` asks the server to work out the agency's current listings
 * itself; the browser never has to enumerate thousands of ids to send back.
 */
export const exportListingsReaxml = (listingIds: string[]) =>
  api.create(url.EXPORT_LISTINGS_REAXML, { listingIds });

/** `listingType` narrows the export to one property type; omit it for all of them. */
export const exportActiveListingsReaxml = (listingType?: string) =>
  api.create(url.EXPORT_LISTINGS_REAXML, {
    scope: "active",
    ...(listingType ? { listingType } : {}),
  });

/** One page of the current-plus-history export. Pass the previous page's
 *  `nextCursor` to continue; omit it for the first page. */
export const exportAllListingsReaxmlPage = (cursor?: unknown) =>
  api.create(url.EXPORT_LISTINGS_REAXML, {
    scope: "all",
    ...(cursor ? { cursor } : {}),
  });

/** Live listing counts per property type, for the per-type export buttons. */
export const getExportListingCounts = () => api.get(url.EXPORT_LISTING_COUNTS, null);

export const uploadListingDomain = (data) =>
  api.create(url.UPLOAD_LISTINGS_DOMAIN, {
    ...data,
    AgentId: getAuthenticatedUser()["DomainAgencyId"],
  });
/** Same payload as uploadListingDomain, but only returns the Domain JSON — nothing is uploaded. */
export const previewListingJsonDomain = (data) =>
  api.create(url.PREVIEW_LISTING_JSON_DOMAIN, {
    ...data,
    AgentId: getAuthenticatedUser()["DomainAgencyId"],
  });
export const uploadListingHomes = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["HomesAgentId"],
    portalType: "homes",
  });
export const uploadListingHomely = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["HomelyAgentId"],
    portalType: "homely",
  });
export const uploadListingRS = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["AgentId"],
    portalType: "RS",
  });
export const uploadListingSoho = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["SohoAgentId"],
    portalType: "soho",
  });
export const uploadListingOTH = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["OTHAgentId"],
    portalType: "oth",
  });
export const uploadListingView = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["ViewAgentId"],
    portalType: "view",
  });
export const uploadListingRent = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["RentAgentId"],
    portalType: "rent",
  });
export const uploadListingRealty = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["RealtyAgentId"],
    portalType: "realty",
  });
export const uploadListingCPG = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["CPGAgentId"],
    portalType: "cpg",
  });
export const uploadListingIrene = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["IreneAgentId"],
    portalType: "irene",
  });

export const uploadListingRateMyAgent = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["RateMyAgentAgentId"],
    portalType: "ratemyagent",
  });

export const uploadListingListingLoop = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["ListingLoopAgentId"],
    portalType: "listingloop",
  });

export const uploadListingFarmBuy = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["FarmBuyAgentId"],
    portalType: "farmbuy",
  });

export const uploadListingHorseProperty = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["HorsePropertyAgentId"],
    portalType: "horseproperty",
  });

export const uploadListingFarmProperty = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["FarmPropertyAgentId"],
    portalType: "farmproperty",
  });

export const uploadListingDevelopmentReady = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["DevelopmentReadyAgentId"],
    portalType: "developmentready",
  });

export const uploadListingREIP = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["REIPAgentId"],
    portalType: "reip",
  });

export const uploadListingPropertyMate = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["PropertyMateAgentId"],
    portalType: "propertymate",
  });

export const uploadListingEasyProperty = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["EasyPropertyAgentId"],
    portalType: "easyproperty",
  });
export const uploadListingRentBuy = (data) =>
  api.create(url.UPLOAD_LISTINGS_REA, {
    ...data,
    AgentId: getAuthenticatedUser()["RentBuyAgentId"],
    portalType: "rentbuy",
  });

export const getREAListingPortals = (listingId) =>
  api.get(url.GET_ALL_REA_PORTALS + "/" + listingId, null);

export const getREAUploadReport = (uploadId) =>
  api.get(url.GET_REA_UPLOAD_REPORT + "/" + uploadId, null);

//LISTING PORTAL

export const getListingPortals = (listingId) =>
  api.get(url.GET_ALL_LISTING_PORTALS + "/" + listingId, null);

// Live per-portal agency IDs from the CMS-edited feed-agentmap record —
// NOT the login-time snapshot on the authenticated user (Trello sALKBatN).
export const getActivatedPortals = () => api.get(url.GET_ACTIVATED_PORTALS, null);

export const getListingPortal = (id) =>
  api.get(url.GET_ONE_PORTAL_BY_ID + "/" + id, null);

export const addListingPortal = (data) =>
  api.create(url.POST_CREAT_PORTAL, data);

export const updateListingPortal = (id, data) =>
  api.update(url.UPDATE_CLIENT_PORTAL + "/" + id, data);

export const deleteListingPortal = (id) =>
  api.delete(url.DELETE_PORTAL + "/" + id, null);

// REA feed processing status — timeline of xml-parse/agent-map/listing-save/etc.
// stages for this listing, as reported by FtpServer's REAFeedProcessor Lambdas.
export const getReaFeedStatusByListing = (listingId) =>
  api.get(url.GET_REA_FEED_STATUS_BY_LISTING + "/" + listingId, null);

//LISTINGER
export const getListingers = (showListing: boolean, showHistoric = false, page?: number, limit?: number, search?: string, filterType?: string, statusFilter?: string, sort?: string, dateFrom?: string, dateTo?: string) => {
  // Retrieve the authenticated user's data from local storage
  const authUserString = localStorage.getItem("authUser");
  if (!authUserString) {
    console.error("Authentication user data is missing.");
    return; // Optionally handle this case as needed
  }
  const authUser = JSON.parse(authUserString);
  const clientId = authUser?.ClientId;
  const userId = authUser?.UserId;

  if (!clientId || !userId) {
    console.error("ClientId or UserId is missing for the authenticated user");
    return; // Optionally handle this case as needed
  }

  // Append the query parameters
  return api.get(
    `${url.GET_ALL_LISTINGERS}/${clientId}?userId=${userId}&allListing=${showListing ? "office" : "agent"}&showHistoric=${showHistoric}${page !== undefined ? `&page=${page}` : ''}${limit !== undefined ? `&limit=${limit}` : ''}${search ? `&search=${encodeURIComponent(search)}` : ''}${filterType ? `&filterType=${encodeURIComponent(filterType)}` : ''}${statusFilter ? `&statusFilter=${encodeURIComponent(statusFilter)}` : ''}${sort ? `&sort=${encodeURIComponent(sort)}` : ''}${dateFrom ? `&dateFrom=${encodeURIComponent(dateFrom)}` : ''}${dateTo ? `&dateTo=${encodeURIComponent(dateTo)}` : ''}`,
    null
  );
};

export const findListingersByAddress = (address: {
  subNumber?: string;
  streetNumber?: string;
  street?: string;
  suburb?: string;
}) => {
  const authUserString = localStorage.getItem("authUser");
  if (!authUserString) {
    console.error("Authentication user data is missing.");
    return;
  }
  const authUser = JSON.parse(authUserString);
  const clientId = authUser?.ClientId;
  if (!clientId) {
    console.error("ClientId is missing for the authenticated user");
    return;
  }
  const params = new URLSearchParams();
  if (address.subNumber) params.set("subNumber", address.subNumber);
  if (address.streetNumber) params.set("streetNumber", address.streetNumber);
  if (address.street) params.set("street", address.street);
  if (address.suburb) params.set("suburb", address.suburb);
  return api.get(`${url.FIND_LISTINGERS_BY_ADDRESS}/${clientId}?${params.toString()}`, null);
};

export const findAppraisalsByAddress = (address: {
  subNumber?: string;
  streetNumber?: string;
  street?: string;
  suburb?: string;
}) => {
  const authUserString = localStorage.getItem("authUser");
  if (!authUserString) {
    console.error("Authentication user data is missing.");
    return;
  }
  const authUser = JSON.parse(authUserString);
  const clientId = authUser?.ClientId;
  if (!clientId) {
    console.error("ClientId is missing for the authenticated user");
    return;
  }
  const params = new URLSearchParams();
  if (address.subNumber) params.set("subNumber", address.subNumber);
  if (address.streetNumber) params.set("streetNumber", address.streetNumber);
  if (address.street) params.set("street", address.street);
  if (address.suburb) params.set("suburb", address.suburb);
  return api.get(`${url.FIND_APPRAISALS_BY_ADDRESS}/${clientId}?${params.toString()}`, null);
};

export const getActiveSoldLeasedListingers = (showListing: boolean) => {
  // Retrieve the authenticated user's data from local storage
  const authUserString = localStorage.getItem("authUser");
  if (!authUserString) {
    console.error("Authentication user data is missing.");
    return; // Optionally handle this case as needed
  }
  const authUser = JSON.parse(authUserString);
  const clientId = authUser?.ClientId;
  const userId = authUser?.UserId;

  if (!clientId || !userId) {
    console.error("ClientId or UserId is missing for the authenticated user");
    return; // Optionally handle this case as needed
  }

  // Append the UserId as a query parameter
  return api.get(
    `${url.GET_ACTIVE__SOLD_LEASED_LISTINGERS}/${clientId}?userId=${userId}&allListing=${showListing ? "office" : "agent"
    }`,
    null
  );
};

// export const getListingers = () => api.get(`${url.GET_ALL_LISTINGERS}/${getAuthenticatedUser()['ClientId']}`, null);
export const getDomainListingers = () =>
  api.get(
    url.GET_ALL_LISTINGERS +
    "/" +
    getAuthenticatedUser()["DomainAgencyId"] +
    "/domain",
    null
  );
export const getListinger = (id) =>
  api.get(url.GET_ONE_LISTINGER_BY_ID + "/" + id, null);

// Clone a listing server-side: returns the detail blob with a NEW listingId,
// status Draft and sale/lease results cleared (Trello 1d92t7n1).
export const copyListinger = (id) =>
  api.get(url.GET_ONE_LISTINGER_BY_ID + "/" + id + "/copy", null);

// export const addListinger = (data) =>
//   api.create(url.POST_CREAT_LISTINGER, {
//     ...data,
//     AgentId: getAuthenticatedUser()["AgentId"],
//   });

export const getAllUniquePropertyTags = () => {
  const authUserString = localStorage.getItem("authUser");
  if (!authUserString) {
    console.error("Authentication user data is missing.");
    return; // Optionally handle this case as needed
  }
  const authUser = JSON.parse(authUserString);
  const clientId = authUser?.ClientId;

  return api.get(url.GET_ALL_UNIQUE_PROPERTY_TAGS + "/" + clientId, null);
};
export const addListinger = async (data) => {
  try {
    const response = await api.create(url.POST_CREAT_LISTINGER, {
      ...data,
      AgentId: getAuthenticatedUser()["AgentId"],
    });
    console.log("Full Axios Response:", response); // Debug: Log full response
    return response; // Return full Axios response
  } catch (error) {
    throw error; // Re-throw to handle it in the calling function
  }
};

export const updateListinger = (id, data) =>
  api.update(url.UPDATE_CLIENT_LISTINGER + "/" + id, data);

export const deleteListinger = (id) =>
  api.delete(url.DELETE_LISTINGER + "/" + id, null);

// APPRAISALS (v2)
export const getAppraisals = (clientId: string, query = "") =>
  api.get(url.GET_ALL_APPRAISALS + "/" + clientId + (query ? `?${query}` : ""), null);

export const getAppraisal = (id) =>
  api.get(url.GET_ONE_APPRAISAL_BY_ID + "/" + id, null);

export const getAppraisalsByContact = (contactId) =>
  api.get(url.GET_APPRAISALS_BY_CONTACT + "/" + contactId, null);

export const addAppraisal = (data) =>
  api.create(url.POST_CREATE_APPRAISAL, {
    ...data,
    AgentId: getAuthenticatedUser()["AgentId"],
  });

export const updateAppraisal = (id, data) =>
  api.update(url.UPDATE_APPRAISAL + "/" + id, data);

export const deleteAppraisal = (id) =>
  api.delete(url.DELETE_APPRAISAL + "/" + id, null);

export const getAppraisalMatches = (id) =>
  api.get(url.APPRAISAL_BASE + "/" + id + "/matches", null);

export const getAppraisalComparables = (id) =>
  api.get(url.APPRAISAL_BASE + "/" + id + "/comparables", null);

export const sendAppraisalEmail = (id, data = {}) =>
  api.create(url.APPRAISAL_BASE + "/" + id + "/send-email", data);

// Appraisal Report (Trello 0xVmvHUb) — polished branded PDF (cover + letter),
// distinct from the plain follow-up email above.
export const sendAppraisalReport = (id, data = {}) =>
  api.create(url.APPRAISAL_BASE + "/" + id + "/send-report", data);

export const getAppraisalPublicShareToken = (appraisalId) =>
  api.get(`${url.SHARE_APPRAISAL_PUBLIC_TOKEN}/${appraisalId}`, null);

export const uploadListingerImage = (data) =>
  api.postFormData(url.UPLOAD_LISTING_IMAGE, data);

export const uploadListingerMedia = (data) =>
  api.postFormData(url.UPLOAD_LISTING_MEDIA, data);

export const getAddressByPlaceId = (data) =>
  api.create(url.GET_ADDRESS_BY_PLACE_ID, data);

// export const createListingsPdf = id => api.get(url.CREATE_LISTING_PDF + "/"
//   + id, null);
export const createListingsPdf = ({ listingId, brochureType, paper = "A4" }: { listingId: string; brochureType: string; paper?: string }) => {
  const requestUrl = `${url.CREATE_LISTING_PDF}/${listingId}?brochureType=${encodeURIComponent(brochureType)}&paper=${encodeURIComponent(paper)}`;
  return api.get(requestUrl, null);
};

export const createCustomPDF = ({ listingId }) => {
  const requestUrl = `${url.CREATE_CUSTOM_PDF}/${listingId}/pdf`;
  return api.get(requestUrl, null);
};

// The signed-in agency's effective brochure menu (DB-driven, CMS-controlled).
export const getBrochureMenu = () => api.get(url.GET_BROCHURE_MENU, null);

// Email Dropbox — the logged-in agent's BCC capture address + settings.
export const getMyEmailDropbox = ({ clientId, agentId }) =>
  api.get(
    `${url.EMAIL_DROPBOX_API_URL}/me?clientId=${encodeURIComponent(
      clientId
    )}&agentId=${encodeURIComponent(agentId)}`,
    null
  );

export const updateMyEmailDropbox = (data) =>
  api.update(`${url.EMAIL_DROPBOX_API_URL}/me`, data);

export const createStockListPdf = (param) =>
  api.get(url.CREATE_STOCKLIST_PDF, param);

//LISTIN Agent

// includeHidden also returns website-hidden (departed) agents — used where
// past listings must still resolve their agent names, not in pickers.
export const getListingAgents = (opts: { includeHidden?: boolean } = {}) =>
  api.get(
    url.LISTING_AGENT_API_URL +
      "/" +
      getAuthenticatedUser()["ClientId"] +
      (opts.includeHidden ? "?includeHidden=1" : ""),
    null
  );

export const getListingAgent = (id) =>
  api.get(url.LISTING_AGENT_API_URL + "/agent/" + id, null);

export const addListingAgent = (data) =>
  api.create(url.LISTING_AGENT_API_URL, {
    ...data,
    ClientId: getAuthenticatedUser()["ClientId"],
  });

export const updateListingAgent = (id, data) =>
  api.update(url.LISTING_AGENT_API_URL + "/" + id, data);

export const deleteListingAgent = (id) =>
  api.delete(url.LISTING_AGENT_API_URL + "/" + id, null);

// Dashboard
export const getDashboardMetrics = (
  clientId: string,
  agentId?: string,
  range: "7d" | "30d" | "90d" = "30d",
  listingType?: string,
  suburbListingType?: string
) =>
  api.get(
    `/api/dashboard/metrics?clientId=${encodeURIComponent(clientId)}&range=${range}${
      agentId ? `&agentId=${encodeURIComponent(agentId)}` : ""
    }${listingType ? `&listingType=${encodeURIComponent(listingType)}` : ""}${
      suburbListingType ? `&suburbListingType=${encodeURIComponent(suburbListingType)}` : ""
    }`,
    null
  );

export const getDashboardTrend = (
  clientId: string,
  agentId?: string,
  range: "7d" | "30d" | "90d" = "90d"
) =>
  api.get(
    `/api/dashboard/trend?clientId=${encodeURIComponent(clientId)}&range=${range}${
      agentId ? `&agentId=${encodeURIComponent(agentId)}` : ""
    }`,
    null
  );

export const getDashboardEnquiryTrend = (
  clientId: string,
  agentId?: string,
  range: "7d" | "30d" | "90d" = "90d",
  listingType?: string
) =>
  api.get(
    `/api/dashboard/enquiry-trend?clientId=${encodeURIComponent(clientId)}&range=${range}${
      agentId ? `&agentId=${encodeURIComponent(agentId)}` : ""
    }${listingType ? `&listingType=${encodeURIComponent(listingType)}` : ""}`,
    null
  );

export const getFailedUploads = (clientId: string) =>
  api.get(
    `/api/listing_portal/get_all_failed_uploads?clientId=${encodeURIComponent(clientId)}`,
    null
  );

//Contact apis
export const createContact = (data) =>
  api.create(url.CONTACT_API_URL, {
    ...data,
    clientId: getAuthenticatedUser()["ClientId"],
  });
export const createContactsBatch = (contacts, agentId?: string) => {
  const user = getAuthenticatedUser();
  return api.create(url.CONTACT_BATCH_API_URL, {
    clientId: user?.["ClientId"],
    ...(agentId ? { AgentId: agentId } : {}),
    contacts,
  });
};
export const getAllContact = (showOfficeContacts: boolean, page?: number, limit?: number, search?: string) =>
  api.create(url.CONTACT_API_URL + "/get_all_contact", {
    clientId: getAuthenticatedUser()["ClientId"],
    email: getAuthenticatedUser()["Email"],
    contacts: showOfficeContacts ? "office" : "agent",
    ...(page !== undefined && { page }),
    ...(limit !== undefined && { limit }),
    ...(search !== undefined && { search }),
  });

export const updateContactAgent = (data) => api.update(url.CONTACT_API_URL + "/updateAgent", data);
export const getUniqueTags = () =>
  api.create(url.CONTACT_API_URL + "/get_unique_tags", {
    clientId: getAuthenticatedUser()["ClientId"],
  });
export const getAnalyzeContact = () =>
  api.create(url.CONTACT_API_URL + "/get_analyze_contact", {
    clientId: getAuthenticatedUser()["ClientId"],
  });
export const getContactById = (id) => api.get(url.CONTACT_API_URL + "/" + id);
export const updateContact = (id, data) =>
  api.update(url.CONTACT_API_URL + "/" + id, data);
export const deleteContact = (id) => api.delete(url.CONTACT_API_URL + "/" + id);

//Preference apis
export const createPreference = (data) =>
  api.create(url.PREFERENCE_API_URL, {
    ...data,
  });
export const getPreferenceById = (id) =>
  api.get(url.PREFERENCE_API_URL + "/" + id);
export const getPreferenceByContactId = (id) =>
  api.get(url.PREFERENCE_API_URL + "/get_by_contact_id/" + id);
export const updatePreference = (id, data) =>
  api.update(url.PREFERENCE_API_URL + "/" + id, data);
export const deletePreference = (id) =>
  api.delete(url.PREFERENCE_API_URL + "/" + id);
export const getMatchedProperties = (id) =>
  api.get(url.PREFERENCE_API_URL + "/get_matched_properties/" + id);

//Tasks apis
export const createTask = (data) =>
  api.create(url.TASK_API_URL, {
    ...data,
    clientId: getAuthenticatedUser()["ClientId"],
    // userId = the assignee. Admins may target a colleague (data.userId);
    // otherwise default to the signed-in user. createdBy is set server-side.
    userId: data.userId ?? getAuthenticatedUser()["UserId"],
  });
export const getAllTask = (data) =>
  api.create(url.TASK_API_URL + "/getTaskById", {
    clientId: getAuthenticatedUser()["ClientId"],
    ...data,
  });

export const getTaskById = (id) => api.get(url.TASK_API_URL + "/" + id);
export const updateTask = (id, data) =>
  api.update(url.TASK_API_URL + "/" + id, data);
export const deleteTask = (id) => api.delete(url.TASK_API_URL + "/" + id);
export const getAnalyzeTask = () =>
  api.create(url.TASK_API_URL + "/get_analyze_task", {
    clientId: getAuthenticatedUser()["ClientId"],
  });

//Blogs apis
export const createBlog = (data) =>
  api.create(url.BLOG_API_URL, {
    ...data,
    clientId: getAuthenticatedUser()["ClientId"],
  });
export const getAllBlog = (data) =>
  api.create(url.BLOG_API_URL + "/getBlogsById", {
    clientId: getAuthenticatedUser()["ClientId"],
    ...data,
  });

export const getBlogById = (id) => api.get(url.BLOG_API_URL + "/" + id);
export const updateBlog = (id, data) =>
  api.update(url.BLOG_API_URL + "/" + id, data);
export const deleteBlog = (id) => api.delete(url.BLOG_API_URL + "/" + id);

//Domain
/*
  Domain OAuth redirect_uri is registered with Domain AND repeated verbatim in
  the backend token exchange (contact.controller.js getTokenOnDomain). All three
  must match exactly, so this stays pointed at the v1 /crm/contacts/all page --
  that page is the only callback handler. Changing it needs Domain to register
  the new URI and the backend updated in the same release.
*/
export const DOMAIN_AUTH_REDIRECT_URI =
  "https://portal2.realsoftware.com.au/crm/contacts/all";

// Where to send the user once the callback page has exchanged the code. Lets
// the v2 UI start the flow and get the user back to /v2/contacts instead of
// stranding them on the v1 page they never asked for.
export const DOMAIN_AUTH_RETURN_KEY = "domainAuthReturnTo";

/*
  Set when the flow was started in a tab of its own. The callback page reads it
  to know it's a throwaway tab: it hands the result back to the tab that opened
  it and closes itself, instead of alerting and navigating in place.

  sessionStorage is what carries it across -- a tab opened with window.open gets
  a copy of the opener's session storage, so the flag (and returnTo) survive the
  round trip out to Domain and back. That copy is one-way, which is why the
  result comes back over broadcastDomainAuthResult rather than through storage.
*/
export const DOMAIN_AUTH_TAB_KEY = "domainAuthNewTab";

export type DomainAuthResult = { ok: boolean; message?: string };

const DOMAIN_AUTH_CHANNEL = "domain-auth-result";

/** Hands the outcome of the code exchange back to the tab that started the flow. */
export const broadcastDomainAuthResult = (result: DomainAuthResult) => {
  try {
    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel(DOMAIN_AUTH_CHANNEL);
      channel.postMessage(result);
      // Let the message leave before the callback tab closes itself.
      setTimeout(() => channel.close(), 300);
      return;
    }
    // No BroadcastChannel: writing localStorage raises a `storage` event in
    // every *other* same-origin tab, which is the delivery we need. The value
    // is cleared straight after -- listeners have already been handed it.
    window.localStorage.setItem(DOMAIN_AUTH_CHANNEL, JSON.stringify(result));
    window.localStorage.removeItem(DOMAIN_AUTH_CHANNEL);
  } catch {
    // Channel/storage unavailable: the starting tab just won't hear back, and
    // its dialog stays on the waiting step for the user to dismiss.
  }
};

/** Listens for that result in the tab that started the flow. Returns an unsubscribe. */
export const onDomainAuthResult = (
  handler: (result: DomainAuthResult) => void
): (() => void) => {
  try {
    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel(DOMAIN_AUTH_CHANNEL);
      channel.onmessage = (event: MessageEvent) =>
        handler(event.data as DomainAuthResult);
      return () => channel.close();
    }
  } catch {
    // Fall through to the storage listener below.
  }
  const onStorage = (event: StorageEvent) => {
    if (event.key !== DOMAIN_AUTH_CHANNEL || !event.newValue) return;
    try {
      handler(JSON.parse(event.newValue) as DomainAuthResult);
    } catch {
      // Malformed payload -- nothing useful to report.
    }
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
};

/**
 * Starts the Domain consent flow.
 *
 * With `newTab`, Domain opens in a tab of its own and the CRM stays put
 * underneath; the callback tab reports back over onDomainAuthResult and closes
 * itself. Returns true when that tab actually opened, false when the flow took
 * over this tab instead (v1 callers, or a blocked popup).
 */
export const authDomain = (
  returnTo?: string,
  opts?: { newTab?: boolean }
): boolean => {
  const newTab = opts?.newTab === true;
  try {
    if (returnTo) {
      window.sessionStorage.setItem(DOMAIN_AUTH_RETURN_KEY, returnTo);
    } else {
      window.sessionStorage.removeItem(DOMAIN_AUTH_RETURN_KEY);
    }
    // Written before window.open: the new tab copies session storage as it is
    // created, so anything set afterwards would never reach it.
    if (newTab) {
      window.sessionStorage.setItem(DOMAIN_AUTH_TAB_KEY, "1");
    } else {
      window.sessionStorage.removeItem(DOMAIN_AUTH_TAB_KEY);
    }
  } catch {
    // Private mode / storage disabled: the flow still works, the user just
    // lands on the v1 callback page instead of being bounced back.
  }
  // Assembled to stay byte-identical to the long-standing v1 URL: redirect_uri
  // interpolated raw and scopes separated by %20. Don't run this through
  // URLSearchParams -- it re-encodes both, and this is a working integration.
  const authUrl =
    "https://auth.domain.com.au/v1/connect/authorize" +
    "?client_id=client_6416634590d3f4d8ba27e1c24c854ffb" +
    `&redirect_uri=${DOMAIN_AUTH_REDIRECT_URI}` +
    "&response_type=code" +
    "&scope=openid%20profile%20api_listings_read%20api_agencies_read%20api_enquiries_read%20offline_access";

  if (newTab) {
    const tab = window.open(authUrl, "_blank");
    if (tab) {
      tab.focus?.();
      return true;
    }
    // Popup blocked. Fall through to this tab rather than dead-ending on a
    // button that looks broken, and drop the flag so the callback page handles
    // the return the v1 way.
    try {
      window.sessionStorage.removeItem(DOMAIN_AUTH_TAB_KEY);
    } catch {
      // Nothing to clean up when storage is unavailable.
    }
  }
  window.location.href = authUrl;
  return false;
};

export const authCodeDomain = (code) => {
  return api.create(url.CONTACT_API_URL + "/auth_code_domain", {
    clientId: getAuthenticatedUser()["ClientId"],
    code: code,
  });
};

// Marketing Campaign
export const createMarketingCampaign = (data) =>
  api.create(url.CREATE_MARKETING_CAMPAIGN, {
    ...data,
    clientId: getAuthenticatedUser()["ClientId"],
    userId: getAuthenticatedUser()["UserId"],
  });
export const getMarketingCampaign = (id) =>
  api.get(url.GET_ONE_MARKETING_CAMPAIGN_BY_ID + "/" + id, null);
export const getAllMarketingCampaigns = () =>
  api.get(
    url.GET_ALL_MARKETING_CAMPAIGNS + "/" + getAuthenticatedUser()["ClientId"],
    null
  );
export const getSystemCampaignsForListing = (listingId) =>
  api.get(
    `${url.GET_ALL_MARKETING_CAMPAIGNS.replace('/all', '/ofi-system')}/${getAuthenticatedUser()["ClientId"]}/${listingId}`,
    null
  );
export const updateMarketingCampaign = (id, data) =>
  api.update(url.UPDATE_MARKETING_CAMPAIGN + "/" + id, data);
export const deleteMarketingCampaign = (id) =>
  api.delete(url.DELETE_MARKETING_CAMPAIGN + "/" + id, null);
export const getTags = () =>
  api.get(
    url.GET_CONTACT_TAGS + "/" + getAuthenticatedUser()["ClientId"],
    null
  );
export const activateMarketingCampaign = async (payload) => {
  return api.create(url.ACTIVATE_MARKETING_CAMPAIGN, payload);
};
export const getCampaignTemplates = () =>
  api.get(
    url.GET_CAMPAIGN_TEMPLATES + "/" + getAuthenticatedUser()["ClientId"],
    null
  );
// Agency-saved designer layouts (the server scopes these to the caller's agency).
export const createCampaignTemplate = (data) =>
  api.create(url.GET_CAMPAIGN_TEMPLATES, {
    ...data,
    clientId: getAuthenticatedUser()["ClientId"],
  });
export const updateCampaignTemplate = (id, data) =>
  api.update(url.GET_CAMPAIGN_TEMPLATES + "/" + id, data);
export const deleteCampaignTemplate = (id) =>
  api.delete(url.GET_CAMPAIGN_TEMPLATES + "/" + id, null);

export const getCampaignOpens = (campaignId) =>
  api.get(`${url.GET_CAMPAIGN_OPENS}/${campaignId}/opens`, null);
export const getCampaignClicks = (campaignId) =>
  api.get(`${url.GET_CAMPAIGN_CLICKS}/${campaignId}/clicks`, null);
export const getCampaignReplies = (campaignId) =>
  api.get(`${url.GET_CAMPAIGN_REPLIES}/${campaignId}/replies`, null);
// Who unsubscribed via this campaign's email (recorded from the link's ?c=).
export const getCampaignUnsubscribes = (campaignId) =>
  api.get(`${url.GET_CAMPAIGN_OPENS}/${campaignId}/unsubscribes`, null);

// --- Reply Inbox (agency-wide captured inbound email) ---
// clientId travels explicitly (like getTags) so grace-mode auth still scopes.
const inboxClientId = () => (getAuthenticatedUser() || {})["ClientId"];
export const getInboxReplies = (params = {}) =>
  api.get(url.INBOX_REPLIES, { clientId: inboxClientId(), ...params });
export const getInboxUnreadCount = () =>
  api.get(url.INBOX_UNREAD_COUNT, { clientId: inboxClientId() });
export const markInboxReplyRead = (id, read = true) =>
  api.create(`${url.INBOX_REPLIES}/${id}/read`, { read, clientId: inboxClientId() });
export const markAllInboxRepliesRead = () =>
  api.create(`${url.INBOX_REPLIES}/read-all`, { clientId: inboxClientId() });

// --- Client support tickets (lodged here, managed in the Real Software CMS) ---
// Attachments upload straight to S3 via presigned PUTs (multipart through the
// Lambda API dies at API Gateway's ~6MB payload cap), so tickets/replies now
// travel as JSON with {name,url} attachment metadata.
export const presignSupportUploads = (files: { name: string; type: string }[]) =>
  api.postJson(url.SUPPORT_UPLOADS, { files, clientId: inboxClientId() || "" });
export const lodgeSupportTicket = (data: {
  subject: string;
  description: string;
  priority: string;
  attachments: { name: string; url: string }[];
}) => api.postJson(url.SUPPORT_TICKETS, { ...data, clientId: inboxClientId() || "" });
export const getSupportTickets = () =>
  api.get(url.SUPPORT_TICKETS, { clientId: inboxClientId() });
export const getSupportTicket = (sk: string) =>
  api.get(`${url.SUPPORT_TICKETS}/${encodeURIComponent(sk)}`, {
    clientId: inboxClientId(),
  });
export const replySupportTicket = (
  sk: string,
  data: { text: string; attachments: { name: string; url: string }[] }
) =>
  api.postJson(`${url.SUPPORT_TICKETS}/${encodeURIComponent(sk)}/comments`, {
    ...data,
    clientId: inboxClientId() || "",
  });

// Function to create branding
export const createBranding = (data) => api.create(url.CREATE_BRANDING, data);

// Function to get branding by ClientId
export const getBrandingByClientId = (clientId) =>
  api.get(`${url.GET_BRANDING_BY_CLIENT_ID}/${clientId}`, null);
export const getContactByClientId = (clientId) =>
  api.get(`${url.GET_CONTACT_BY_CLIENT_ID}/${clientId}`, null);

// Replace the agency's full hero/heading media list (clientId-scoped, stored on
// branding.HeroImages). Used for add / remove / reorder. heroImages is a flat
// array of file URLs; the website reads them back from getBrandingByClientId.
// heroDisplayMode ("image" | "video") is the persisted Show Images/Show Video
// choice — omit it to leave the stored mode untouched.
export const setHeroImages = (clientId, heroImages, heroDisplayMode?: "image" | "video") =>
  api.update(`${url.SET_HERO_IMAGES}/${clientId}`, { heroImages, heroDisplayMode });

export const getEmailPreview = async (data) => {
  try {
    // Directly return the response from the API call
    return await api.create(url.GET_EMAIL_PREVIEW, data);
  } catch (err) {
    console.error("Error fetching email preview:", err);
    throw err;
  }
};

// Function to update branding
// export const updateBranding = (brandingId, data) => api.update(`${url.UPDATE_BRANDING}/${brandingId}`, data);
export const updateBranding = async (clientId, data) => {
  return api.update(`${url.UPDATE_BRANDING}/${clientId}`, data);
};

// Function to update social URLs
export const updateSocialUrls = async (clientId, data) => {
  return api.update(`${url.UPDATE_SOCIAL_URLS}/${clientId}`, data);
};

// Assuming api.update correctly makes a PUT request and the backend is expecting a PUT request
export const updateContactDynamoDB = async (clientId, contactData) => {
  return api.update(`${url.UPDATE_CONTACT_INFO}/${clientId}`, contactData);
};

// Automations — per-agency CRM automation config.
export const getAutomationSettings = (clientId) =>
  api.get(`${url.GET_AUTOMATION_SETTINGS}/${clientId}`, null);
export const updateAutomationSettings = (clientId, settings) =>
  api.update(`${url.UPDATE_AUTOMATION_SETTINGS}/${clientId}`, { settings });

// Vendor report — email the shareable link to the vendor (or a given email).
export const sendVendorReport = (data) => api.create(url.SEND_VENDOR_REPORT, data);

// Offer form — email the shareable offer-form link to a contact.
export const sendOfferFormLink = (data) => api.create(url.SEND_OFFER_FORM_LINK, data);

// Advertising / VPA (§24).
export const getMarketingCatalog = (clientId) =>
  api.get(`${url.ADVERTISING_CATALOG}/${clientId}`, null);
export const updateMarketingCatalog = (clientId, catalog) =>
  api.update(`${url.ADVERTISING_CATALOG}/${clientId}`, { catalog });
export const getListingAdvertising = (listingId) =>
  api.get(`${url.ADVERTISING_LISTING}/${listingId}`, null);
export const saveListingAdvertising = (listingId, advertising) =>
  api.update(`${url.ADVERTISING_LISTING}/${listingId}`, { advertising });
export const sendAdvertisingReport = (data) => api.create(url.SEND_ADVERTISING_REPORT, data);
export const getAdvertisingInvoiceSettings = (clientId) =>
  api.get(`${url.ADVERTISING_INVOICE_SETTINGS}/${clientId}`, null);
export const updateAdvertisingInvoiceSettings = (clientId, settings) =>
  api.update(`${url.ADVERTISING_INVOICE_SETTINGS}/${clientId}`, { settings });

// Listing documents (§8/§33).
export const getListingDocuments = (listingId) =>
  api.get(`${url.LISTING_DOCUMENTS}/${listingId}`, null);
export const saveListingDocuments = (listingId, documents) =>
  api.update(`${url.LISTING_DOCUMENTS}/${listingId}`, { documents });

// Signed public share token (KAN-115). Authed; the backend reads clientId from
// the caller's JWT and signs { c, l } with the server secret. The browser drops
// the returned token into the public offer/tenancy/vendor-report/advertising/
// documents links. Token rides the existing x-access-token header.
// groupId (VIC tenancy co-applicant households only) embeds the household in
// the signed token, so re-copying the link from the CRM to manually send a
// skipped invite still attaches the recipient to the same group.
export const getPublicShareToken = (listingId, groupId?: string) =>
  api.get(
    `${url.SHARE_PUBLIC_TOKEN}/${listingId}${groupId ? `?groupId=${encodeURIComponent(groupId)}` : ""}`,
    null
  );

// The listing's live page on the agency's own website (404 until pushed there).
export const getWebsitePropertyUrl = (listingId) =>
  api.get(`${url.SHARE_PROPERTY_URL}/${listingId}`, null);

// AI assistant (Claude).
export const aiPropertyDescription = (data) => api.create(url.AI_PROPERTY_DESCRIPTION, data);
export const aiBuyerFeedback = (data) => api.create(url.AI_BUYER_FEEDBACK, data);
export const aiDraftReply = (data) => api.create(url.AI_DRAFT_REPLY, data);
export const aiEmailCampaign = (data) => api.create(url.AI_EMAIL_CAMPAIGN, data);
export const aiSupportChat = (data) => api.create(url.AI_SUPPORT_CHAT, data);

// Tag management (§20) — clientId comes from the session, like getUniqueTags.
export const renameTag = ({ from, to }: { from: string; to: string }) =>
  api.create(url.CONTACT_RENAME_TAG_API_URL, {
    clientId: getAuthenticatedUser()["ClientId"],
    from,
    to,
  });
export const deleteTag = ({ tag }: { tag: string }) =>
  api.create(url.CONTACT_DELETE_TAG_API_URL, {
    clientId: getAuthenticatedUser()["ClientId"],
    tag,
  });

// Duplicate contacts — the agency comes from the session token server-side, so
// no clientId is sent.
export const getContactDuplicates = (summary = false) =>
  api.get(url.CONTACT_DUPLICATES, summary ? { summary: "1" } : null);
export const previewContactMerge = (data: { contactIds: string[]; survivorId: string }) =>
  api.create(url.CONTACT_DUPLICATES_PREVIEW, data);
export const mergeContactDuplicates = (data: { contactIds: string[]; survivorId: string }) =>
  api.create(url.CONTACT_DUPLICATES_MERGE, data);
export const dismissContactDuplicates = (contactIds: string[]) =>
  api.create(url.CONTACT_DUPLICATES_DISMISS, { contactIds });
export const restoreContactDuplicates = (dismissalId: string) =>
  api.delete(`${url.CONTACT_DUPLICATES_DISMISS}/${encodeURIComponent(dismissalId)}`);

// Server-side image watermarking — applies the agency logo to uploaded listing
// photos (returns the same URLs, now watermarked in S3).
export const watermarkMedia = (data) => api.create(url.WATERMARK_MEDIA, data);

// Social media connections (per-agency).
export const getSocialConnections = (clientId) =>
  api.get(`${url.SOCIAL_CONNECTIONS}/${clientId}`, null);
export const updateSocialConnections = (clientId, data) =>
  api.update(`${url.SOCIAL_CONNECTIONS}/${clientId}`, data);

// Trust accounting.
export const getTrustAccount = (clientId) =>
  api.get(`${url.TRUST_ACCOUNT}/${clientId}`, null);
export const saveTrustAccount = (clientId, data) =>
  api.update(`${url.TRUST_ACCOUNT}/${clientId}`, data);
export const recordTrustDeposit = (data) => api.create(url.TRUST_DEPOSIT, data);
export const getTrustLedger = (listingId, clientId) =>
  api.get(`${url.TRUST_LEDGER}/${listingId}${clientId ? `?clientId=${encodeURIComponent(clientId)}` : ""}`, null);
export const emailTrustSolicitorLetter = (receiptId, data) =>
  api.create(`${url.TRUST_SOLICITOR_LETTER}/${receiptId}/send`, data || {});
export const trustSettlement = (data) => api.create(url.TRUST_SETTLEMENT, data);
export const trustDisburse = (data) => api.create(url.TRUST_DISBURSEMENT, data);
export const trustReverse = (data) => api.create(url.TRUST_REVERSE, data);
export const trustReconcile = (data) => api.create(url.TRUST_RECONCILIATION, data);
export const listTrustReconciliations = (clientId) =>
  api.get(`${url.TRUST_RECONCILIATION}/${clientId}`, null);
export const verifyTrustReconciliation = (id, data) =>
  api.create(`${url.TRUST_RECONCILIATION}/${id}/verify`, data || {});
export const trustDashboard = (clientId) => api.get(`${url.TRUST_DASHBOARD}/${clientId}`, null);
export const trustListLedgers = (clientId) => api.get(`${url.TRUST_LEDGERS}/${clientId}`, null);
export const trustLedgerDetail = (ledgerId) => api.get(`${url.TRUST_LEDGER_DETAIL}/${ledgerId}`, null);
export const trustListReceipts = (clientId) => api.get(`${url.TRUST_RECEIPTS}/${clientId}`, null);
export const trustListPayments = (clientId) => api.get(`${url.TRUST_PAYMENTS}/${clientId}`, null);
export const trustMatchStatement = (data) => api.create(url.TRUST_MATCH, data);
export const trustGenerateAba = (data) => api.create(url.TRUST_ABA, data);

// Contracts (sale lifecycle — sits on top of trust accounting).
export const contractsList = (clientId) => api.get(`${url.CONTRACTS_LIST}/${clientId}`, null);
export const contractDetail = (contractId) => api.get(`${url.CONTRACTS_DETAIL}/${contractId}`, null);
export const contractByListing = (clientId, listingId) =>
  api.get(`${url.CONTRACTS_BY_LISTING}/${clientId}/${listingId}`, null);
export const createContract = (data) => api.create(url.CONTRACTS_CREATE, data);
export const createContractFromListing = (data) => api.create(url.CONTRACTS_FROM_LISTING, data);
export const updateContract = (contractId, data) => api.update(`${url.CONTRACTS_UPDATE}/${contractId}`, data);
export const cancelContract = (contractId, data) => api.create(`${url.CONTRACTS_CANCEL}/${contractId}`, data || {});
export const emailContractSalesAdvice = (contractId, data) =>
  api.create(`${url.CONTRACTS_SALES_ADVICE}/${contractId}/send`, data || {});
export const settleContract = (contractId, data) =>
  api.create(`${url.CONTRACTS_SETTLE}/${contractId}`, data || {});
export const getContractsSettings = (clientId) => api.get(`${url.CONTRACTS_SETTINGS}/${clientId}`, null);
export const updateContractsSettings = (clientId, settings) =>
  api.update(`${url.CONTRACTS_SETTINGS}/${clientId}`, { settings });

// Leased (lease-deal lifecycle — pipeline view over PM tenancies).
export const leasedList = (clientId) => api.get(`${url.LEASED_LIST}/${clientId}`, null);
export const leasedDetail = (tenancyId) => api.get(`${url.LEASED_DETAIL}/${tenancyId}`, null);
export const updateLeaseMilestone = (tenancyId, data) => api.update(`${url.LEASED_MILESTONE}/${tenancyId}`, data);

// Lease agreements (internal pre-tenancy lease deal on the listing).
export const leaseAgreementsList = (clientId) => api.get(`${url.LEASE_AGREEMENTS_LIST}/${clientId}`, null);
export const leaseAgreementDetail = (agreementId) => api.get(`${url.LEASE_AGREEMENTS_DETAIL}/${agreementId}`, null);
export const leaseAgreementByListing = (clientId, listingId) =>
  api.get(`${url.LEASE_AGREEMENTS_BY_LISTING}/${clientId}/${listingId}`, null);
export const createLeaseAgreement = (data) => api.create(url.LEASE_AGREEMENTS_CREATE, data);
export const createLeaseAgreementFromListing = (data) => api.create(url.LEASE_AGREEMENTS_FROM_LISTING, data);
export const updateLeaseAgreement = (agreementId, data) => api.update(`${url.LEASE_AGREEMENTS_UPDATE}/${agreementId}`, data);
export const cancelLeaseAgreement = (agreementId, data) => api.create(`${url.LEASE_AGREEMENTS_CANCEL}/${agreementId}`, data || {});
export const activateLeaseAgreement = (agreementId, data) => api.create(`${url.LEASE_AGREEMENTS_ACTIVATE}/${agreementId}`, data || {});

// Property management / rental trust accounting.
export const pmGetAccount = (clientId) => api.get(`${url.PM_ACCOUNT}/${clientId}`, null);
export const pmSaveAccount = (clientId, data) => api.update(`${url.PM_ACCOUNT}/${clientId}`, data);
export const pmGetSettings = (clientId) => api.get(`${url.PM_SETTINGS}/${clientId}`, null);
export const pmUpdateSettings = (clientId, data) => api.update(`${url.PM_SETTINGS}/${clientId}`, data);
export const pmListManagements = (clientId) => api.get(`${url.PM_MANAGEMENTS}/${clientId}`, null);
export const pmGetManagement = (managementId) => api.get(`${url.PM_MANAGEMENT}/${managementId}`, null);
export const pmCreateManagement = (data) => api.create(url.PM_MANAGEMENT, data);
export const pmUpdateManagement = (id, data) => api.update(`${url.PM_MANAGEMENT}/${id}`, data);
export const pmCloseManagement = (id, data) => api.create(`${url.PM_MANAGEMENT}/${id}/close`, data || {});
export const pmCreateFromListing = (data) => api.create(url.PM_FROM_LISTING, data);
export const pmCreateTenancy = (data) => api.create(url.PM_TENANCY, data);
export const pmUpdateTenancy = (id, data) => api.update(`${url.PM_TENANCY}/${id}`, data);
export const pmVacateTenancy = (id, data) => api.create(`${url.PM_TENANCY}/${id}/vacate`, data || {});
export const pmRentReview = (id, data) => api.create(`${url.PM_TENANCY}/${id}/rent-review`, data);
export const pmRecordReceipt = (data) => api.create(url.PM_RECEIPT, data);
export const pmReverse = (data) => api.create(url.PM_REVERSE, data);
export const pmDashboard = (clientId) => api.get(`${url.PM_DASHBOARD}/${clientId}`, null);
export const pmOnboarding = (clientId) => api.get(`${url.PM_ONBOARDING}/${clientId}`, null);
export const pmRentBook = (clientId) => api.get(`${url.PM_RENT_BOOK}/${clientId}`, null);
export const pmListReceipts = (clientId) => api.get(`${url.PM_RECEIPTS}/${clientId}`, null);
export const pmListPayments = (clientId) => api.get(`${url.PM_PAYMENTS}/${clientId}`, null);
export const pmBankImport = (data) => api.create(url.PM_BANK_IMPORT, data);
export const pmConfirmBankImport = (data) => api.create(url.PM_BANK_IMPORT_CONFIRM, data);
export const pmIgnoreBankTransaction = (id, data) => api.create(`${url.PM_BANK_TRANSACTION}/${id}/ignore`, data || {});
// R2 — money out
export const pmListCreditorBills = (clientId, query = "") => api.get(`${url.PM_CREDITOR_BILLS}/${clientId}${query}`, null);
export const pmCreateCreditorBill = (data) => api.create(url.PM_CREDITOR_BILL, data);
export const pmUpdateCreditorBill = (id, data) => api.update(`${url.PM_CREDITOR_BILL}/${id}`, data);
export const pmCancelCreditorBill = (id, data) => api.create(`${url.PM_CREDITOR_BILL}/${id}/cancel`, data || {});
export const pmDisburseManagement = (data) => api.create(url.PM_DISBURSE, data);
export const pmRunDisbursements = (data) => api.create(url.PM_DISBURSE_RUN, data);
export const pmRunAba = (runId, data) => api.create(`${url.PM_DISBURSE_RUN}/${runId}/aba`, data || {});
export const pmListStatements = (managementId) => api.get(`${url.PM_STATEMENTS}/${managementId}`, null);
// R3 — bonds, reconciliation, audit
export const pmCreateBond = (data) => api.create(url.PM_BOND, data);
export const pmUpdateBond = (id, data) => api.update(`${url.PM_BOND}/${id}`, data);
export const pmSetBondStatus = (id, data) => api.create(`${url.PM_BOND}/${id}/status`, data);
export const pmListBonds = (clientId, query = "") => api.get(`${url.PM_BONDS}/${clientId}${query}`, null);
export const pmReconcile = (data) => api.create(url.PM_RECONCILIATION, data);
export const pmListReconciliations = (clientId) => api.get(`${url.PM_RECONCILIATION}/${clientId}`, null);
export const pmVerifyReconciliation = (id, data) => api.create(`${url.PM_RECONCILIATION}/${id}/verify`, data || {});
// R4 — operations
export const pmListWorkOrders = (clientId, query = "") => api.get(`${url.PM_WORK_ORDERS}/${clientId}${query}`, null);
export const pmCreateWorkOrder = (data) => api.create(url.PM_WORK_ORDER, data);
export const pmUpdateWorkOrder = (id, data) => api.update(`${url.PM_WORK_ORDER}/${id}`, data);
export const pmSetWorkOrderStatus = (id, data) => api.create(`${url.PM_WORK_ORDER}/${id}/status`, data);
export const pmListInspections = (clientId, query = "") => api.get(`${url.PM_INSPECTIONS}/${clientId}${query}`, null);
export const pmCreateInspection = (data) => api.create(url.PM_INSPECTION, data);
export const pmUpdateInspection = (id, data) => api.update(`${url.PM_INSPECTION}/${id}`, data);
export const pmCompleteInspection = (id, data) => api.create(`${url.PM_INSPECTION}/${id}/complete`, data || {});
export const pmDueSummary = (clientId, query = "") => api.get(`${url.PM_DUE_SUMMARY}/${clientId}${query}`, null);
export const pmPortalLink = (kind, id) => api.get(`${url.PM_PORTAL_LINK}/${kind}/${id}`, null);

// External portal account provisioning (owner/tenant/vendor logins)
export const portalInvite = (data) => api.create(url.PORTAL_ADMIN_INVITE, data);
export const portalListUsers = (query = "") => api.get(`${url.PORTAL_ADMIN_USERS}${query}`, null);
export const portalResendInvite = (externalUserId) => api.create(`${url.PORTAL_ADMIN_RESEND}/${externalUserId}`, {});
export const portalDisableUser = (externalUserId) => api.create(`${url.PORTAL_ADMIN_DISABLE}/${externalUserId}`, {});
export const portalEligible = (contactId) => api.get(`${url.PORTAL_ADMIN_ELIGIBLE}/${contactId}`, null);
export const portalGrantContact = (data) => api.create(url.PORTAL_ADMIN_GRANT_CONTACT, data);
export const portalRevokeGrant = (data) => api.delete(url.PORTAL_ADMIN_GRANT, { data });
export const pmEmailStatement = (id, data) => api.create(`${url.PM_STATEMENT}/${id}/email`, data || {});
export const pmRemindArrears = (data) => api.create(url.PM_ARREARS_REMIND, data || {});

// Note APIs
export const createNote = (data) => api.create(url.CREATE_NOTE, data);

export const getNotesByContactId = (contactId) =>
  api.get(`${url.GET_NOTES_BY_CONTACT_ID}/${contactId}`);

export const getContactEmailActivityByContactId = (contactId) =>
  api.get(`${url.CONTACT_EMAIL_ACTIVITY_API_URL}/${contactId}`);

export const getEmailActivityByListing = (listingId) =>
  api.get(`${url.CONTACT_EMAIL_ACTIVITY_API_URL}/listing/${listingId}`);

// Listing marketing segments (Trello #435) — Eagle-style per-listing contact lists
export const getListingMarketingSegments = (listingId) =>
  api.get(`${url.LISTING_MARKETING_API_URL}/${listingId}/segments`);

export const getListingMarketingSegmentContacts = (listingId, segmentKey) =>
  api.get(`${url.LISTING_MARKETING_API_URL}/${listingId}/segments/${segmentKey}/contacts`);

export const createContactEmailActivity = (data) =>
  api.create(url.CONTACT_EMAIL_ACTIVITY_API_URL, {
    ...data,
    clientId: data?.clientId ?? getAuthenticatedUser()?.["ClientId"],
  });

export const updateNote = (id, data) =>
  api.update(`${url.UPDATE_NOTE}/${id}`, data);

export const deleteNote = (id) => api.delete(`${url.DELETE_NOTE}/${id}`);

export const getContactListings = (contactId, clientId, data) =>
  api.get(`${url.GET_CONTACT_LISTINGS}/${contactId}/listings`, { ...data, clientId });

export const associateListing = (data) =>
  api.create(url.POST_ASSOCIATE_LISTING, data);

export const updateAssociateListing = (data) =>
  api.update(url.POST_ASSOCIATE_LISTING, data);

export const deleteAssociateListing = (data) =>
  api.delete(url.POST_ASSOCIATE_LISTING, { data });

export const getListingContacts = (listingId, clientId) =>
  api.get(`${url.GET_LISTING_CONTACTS_NEW}/${listingId}/${clientId}/contacts`);

